/**
 * HearingReportEditModal — Re-open a saved report snapshot with the report
 * builder pre-filled, allow the audiologist to tweak findings /
 * recommendations / provisional diagnosis / referred-by / license, then
 * Save to overwrite the SAME saved version (no new version created for
 * pure narrative edits — keeps the history strip tidy).
 *
 * The underlying audiogram / impedance / speech data stays FROZEN — this
 * modal only exposes the builder-side fields (see PATCH
 * /api/hearing-reports/{version_id} on the backend).
 *
 * Props:
 *   versionId : string   — required
 *   onClose   : ()   → void  — closes the modal
 *   onSaved   : ({version_id, label}) → void  — bubbles up so the parent
 *                              History modal can refresh its list.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Save } from 'lucide-react';
import ReportsPanel from './ReportsPanel';
import { useAuth } from '../AuthContext';

import useEscapeKey from '../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HearingReportEditModal({ versionId, onClose, onSaved }) {
  const { user } = useAuth();
  useEscapeKey(onClose);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState('');
  // The Reports Panel debounces its onPersist call — we mirror the latest
  // payload here so the explicit "Save changes" button can flush it too.
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!versionId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/hearing-reports/${versionId}`);
        if (!alive) return;
        setData(r.data);
        setDraft(r.data?.snapshot?.builder || {});
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || 'Could not load saved report');
      }
    })();
    return () => { alive = false; };
  }, [versionId]);

  const handleSave = async () => {
    if (!versionId || saving) return;
    setSaving(true); setSaveMsg('');
    try {
      const payload = {
        clinical_impression: draft.clinical_impression ?? '',
        findings_by_section: draft.findings_by_section ?? {},
        recommendations: Array.isArray(draft.recommendations)
          ? draft.recommendations
          : (draft.recommendations || '').split('\n').map((l) => l.trim()).filter(Boolean),
        further_advice: draft.further_advice ?? '',
        provisional_diagnosis: draft.provisional_diagnosis ?? '',
        referred_by: draft.referred_by ?? '',
        license: draft.license ?? '',
        sections: draft.sections || [],
      };
      const r = await axios.patch(`${API}/hearing-reports/${versionId}`, payload);
      setSaveMsg('Changes saved');
      // Persistent stamp so audiologists always see confirmation even
      // after the transient toast fades — clears the "did my Save
      // actually go through?" moment.
      try {
        const now = new Date();
        setLastSavedAt(now.toLocaleString('en-IN', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
        }));
      } catch { /* Intl edge — ignore */ }
      // Extended toast window (was 2 s) so screen-readers + tests have
      // time to observe the confirmation before it disappears.
      setTimeout(() => setSaveMsg(''), 4000);
      onSaved?.(r?.data || { version_id: versionId });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
      setTimeout(() => setErr(''), 3000);
    } finally { setSaving(false); }
  };

  if (!versionId) return null;

  return (
    <div
      className="fixed inset-0 z-[75] bg-slate-900/70 flex flex-col past-report-viewer"
      data-testid="hearing-report-edit-modal"
    >
      {/* Toolbar */}
      <div className="past-report-viewer-toolbar bg-amber-900 text-white px-4 py-2 flex items-center justify-between flex-shrink-0 gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold truncate">
            Edit saved report{data?.label ? ` · ${data.label}` : ''}
          </div>
          {data?.patient_name && (
            <div className="text-[11px] text-amber-200 truncate">
              {data.patient_name}{data.patient_mrd ? ` · ${data.patient_mrd}` : ''}
            </div>
          )}
          <div className="text-[10px] font-bold uppercase tracking-wide bg-amber-500/30 text-amber-100 px-1.5 py-0.5 rounded inline-block mt-0.5">
            Editing · builder fields only
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {saveMsg && (
            <span
              className="text-[11px] font-semibold text-emerald-200"
              data-testid="hearing-report-edit-save-msg"
            >{saveMsg}</span>
          )}
          {!saveMsg && lastSavedAt && (
            <span
              className="text-[11px] font-semibold text-amber-200/80"
              data-testid="hearing-report-edit-last-saved"
              title={`Last saved at ${lastSavedAt}`}
            >Saved · {lastSavedAt}</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!data || saving}
            data-testid="hearing-report-edit-save"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={13} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="hearing-report-edit-close"
            title="Close (Esc)"
            className="w-8 h-8 rounded hover:bg-white/10 flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {err && (
        <div
          className="past-report-viewer-toolbar bg-rose-600 text-white text-xs font-semibold px-4 py-1.5 text-center"
          data-testid="hearing-report-edit-error"
        >{err}</div>
      )}

      <div className="flex-1 overflow-auto bg-slate-200">
        {!data && !err && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-slate-400" />
          </div>
        )}
        {data && (
          <ReportsPanel
            patient={data.snapshot?.patient || {}}
            rightEarData={data.snapshot?.right_ear_audiogram || {}}
            leftEarData={data.snapshot?.left_ear_audiogram || {}}
            preTestData={data.snapshot?.pre_test_data || {}}
            impedanceData={data.snapshot?.impedance_data || {}}
            speechData={data.snapshot?.speech_data || {}}
            specialTestsData={data.snapshot?.special_tests_data || {}}
            oaeData={data.snapshot?.oae_data || {}}
            soundfieldData={data.snapshot?.soundfield_data || {}}
            abrData={data.snapshot?.abr_data || {}}
            pediatricData={data.snapshot?.pediatric_data || {}}
            tinnitusData={data.snapshot?.tinnitus_data || {}}
            sessionId={data.snapshot?.session?.session_id}
            audiologistName={data.snapshot?.audiologist?.name || user?.name || ''}
            audiologistUserId={data.snapshot?.audiologist?.user_id || user?.user_id || ''}
            clinicalImpression={data.snapshot?.builder?.clinical_impression || ''}
            recommendations={data.snapshot?.builder?.recommendations || []}
            audiogramMode="separate"
            initialBuilder={data.snapshot?.builder || {}}
            hideBuilder={false}
            previewId="report-preview-past"
            onPersist={(partial) => setDraft((prev) => ({ ...prev, ...partial }))}
          />
        )}
      </div>
    </div>
  );
}
